import { conversationsApi, ConversationsApiError } from '@/services/conversationsApi'
import { log } from '@/lib/logger'

/**
 * Tag interface matching our backend Tag model
 */
export interface Tag {
  id: string;
  name: string;
  color: string;
  description?: string;
  tag_type?: string;
  created_at?: string;
  updated_at?: string;
}

type RawTag = Omit<Tag, 'tag_type'> & { tag_type?: string | null }

/**
 * Tag creation payload
 */
export interface CreateTagPayload {
  name: string;
  color: string;
  description?: string;
  tag_type: 'user' | 'conversation' | 'system' | 'document' | 'property' | 'universal';
}

/**
 * Tag update payload
 */
export interface UpdateTagPayload {
  name?: string;
  color?: string;
  description?: string;
}

export interface TagQueryParams {
  type?: string;
  search?: string;
  orderBy?: 'name' | 'color' | 'created_at' | 'updated_at';
  order?: 'asc' | 'desc';
}

/**
 * Service for interacting with the Tags API
 */
export class TagService {
  private tagsCache = new Map<string, { data: unknown, timestamp: number }>()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  private readonly serviceName = 'TagService'

  /**
   * Normalize potentially loose backend payloads into our strict Tag shape.
   */
  private normalizeTag(tag: RawTag): Tag {
    const allowedTypes = ['user', 'conversation', 'system', 'document', 'property', 'universal'] as const
    const type = allowedTypes.includes(tag.tag_type as (typeof allowedTypes)[number])
      ? (tag.tag_type as Tag['tag_type'])
      : 'document'

    return {
      ...tag,
      tag_type: type
    }
  }

  /**
   * Enhanced error handling with service-specific logging
   */
  private handleError(error: unknown, operation: string): never {
    const message = error instanceof ConversationsApiError 
      ? error.message 
      : `${this.serviceName} ${operation} failed: ${error}`
    
    log.error(message, { operation, error })
    throw new Error(message)
  }

  /**
   * Accept both array payloads and wrapped API responses without crashing UI consumers.
   */
  private extractTags(response: unknown): RawTag[] {
    if (Array.isArray(response)) {
      return response as RawTag[]
    }

    if (response && typeof response === 'object') {
      const maybeData = (response as { data?: unknown }).data
      if (Array.isArray(maybeData)) {
        return maybeData as RawTag[]
      }

      const maybeResults = (response as { results?: unknown }).results
      if (Array.isArray(maybeResults)) {
        return maybeResults as RawTag[]
      }
    }

    return []
  }

  /**
   * Get all tags
   */
  async getTags(params: TagQueryParams = {}): Promise<Tag[]> {
    try {
      const response = await conversationsApi.getTags({
        type: params.type,
        search: params.search,
        order_by: params.orderBy,
        order: params.order,
      })
      const data = this.extractTags(response)
      return data.map((tag) => this.normalizeTag(tag))
    } catch (error) {
      log.warn(`${this.serviceName} getTags failed; returning empty list`, { error, params })
      return []
    }
  }

  /**
   * Get tags by type
   */
  async getTagsByType(type: string): Promise<Tag[]> {
    const cacheKey = `tags-${type}`
    const cached = this.tagsCache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data as Tag[]
    }

    try {
      const response = await conversationsApi.getTags({ type })
      const data = this.extractTags(response)
      const normalized = data.map((tag) => this.normalizeTag(tag))
      const filtered = normalized.filter((tag) => tag.tag_type === type)
      
      this.tagsCache.set(cacheKey, { data: filtered, timestamp: Date.now() })
      return filtered
    } catch (error) {
      log.warn(`${this.serviceName} getTagsByType failed; returning empty list`, { error, type })
      return []
    }
  }

  /**
   * Get a specific tag by ID
   */
  async getTag(id: string): Promise<Tag> {
    try {
      const response = await conversationsApi.getTag(id)
      return this.normalizeTag(response as RawTag)
    } catch (error) {
      this.handleError(error, 'getTag')
    }
  }

  /**
   * Create a new tag
   */
  async createTag(payload: CreateTagPayload): Promise<Tag> {
    try {
      const response = await conversationsApi.createTag(payload)
      
      // Clear cache to ensure fresh data
      this.tagsCache.clear()
      
      const tagData = this.normalizeTag(response as RawTag)
      return tagData
    } catch (error) {
      this.handleError(error, 'createTag')
    }
  }

  /**
   * Update an existing tag
   */
  async updateTag(id: string, payload: UpdateTagPayload): Promise<Tag> {
    try {
      const response = await conversationsApi.updateTag(id, payload)
      
      // Clear cache to ensure fresh data
      this.tagsCache.clear()
      
      return this.normalizeTag(response as RawTag)
    } catch (error) {
      this.handleError(error, 'updateTag')
    }
  }

  /**
   * Delete a tag
   */
  async deleteTag(id: string): Promise<void> {
    try {
      await conversationsApi.deleteTag(id)
      
      // Clear cache to ensure fresh data
      this.tagsCache.clear()
    } catch (error) {
      this.handleError(error, 'deleteTag')
    }
  }

  /**
   * Clear the tags cache
   */
  clearCache(): void {
    this.tagsCache.clear()
  }
}

// Export a singleton instance
export const tagService = new TagService()
